import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  PlusIcon,
  TrashIcon,
  EyeIcon,
  Cog6ToothIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  DocumentArrowDownIcon,
  DocumentArrowUpIcon,
  ArrowUturnLeftIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { FormField, FieldType, Form } from '../types/Form';
import AutoComplete from './AutoComplete';
import MultiSelectAutoComplete from './MultiSelectAutoComplete';
import api from '../services/api';
import { uploadFormBanner, removeFormBanner } from '../services/formApi';
import { LeadSource } from '../types/Lead';
import { buildFormStructureFromFields, buildFieldsFromFormStructure } from './forms/formStructureSerializer';
import FormBuilderFieldLibrary from './forms/FormBuilderFieldLibrary';

// Constants
const DEFAULT_SECTION_NAME = 'Main Section';
const MAX_FIELDS = 100;
const MAX_FORM_NAME_LENGTH = 200;
const MAX_SECTION_NAME_LENGTH = 100;
const DEBOUNCE_DELAY = 300;

// Reserved field names that shouldn't be used
const RESERVED_FIELD_NAMES = [
  'constructor', '__proto__', 'prototype', 'submit', 'reset', 
  'length', 'name', 'toString', 'valueOf', 'hasOwnProperty'
];

// Required personal information fields that must always exist (cannot be deleted)
const REQUIRED_PERSONAL_FIELDS = ['first_name', 'last_name', 'email', 'phone'];

interface FormBuilderProps {
  form?: Form;
  onSave: (formData: Partial<Form>) => void;
  onClose: () => void;
  isOpen: boolean;
  onBannerChange?: (bannerUrl: string | null) => void;
}

interface FormBuilderField extends FormField {
  id: string; // Always required, unique identifier
}

interface ValidationError {
  field: string;
  message: string;
}

interface HistoryState {
  fields: FormBuilderField[];
  formName: string;
  formDescription: string;
  defaultLeadSourceId?: number | null;
}

// Utility Functions
const generateUniqueId = (): string => {
  // Use crypto.randomUUID if available, fallback to timestamp + random
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

const sanitizeFieldName = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '_')
    .replace(/^[0-9]/, '_$&')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
};

const validateFieldName = (name: string, existingFields: FormBuilderField[], currentFieldId?: string): string | null => {
  if (!name || name.trim() === '') {
    return 'Field name is required';
  }

  const sanitized = sanitizeFieldName(name);
  
  if (sanitized !== name) {
    return 'Field name can only contain lowercase letters, numbers, and underscores';
  }

  if (RESERVED_FIELD_NAMES.includes(name)) {
    return `"${name}" is a reserved word and cannot be used`;
  }

  const duplicate = existingFields.find(f => f.name === name && f.id !== currentFieldId);
  if (duplicate) {
    return `Field name "${name}" is already used`;
  }

  return null;
};

const validateField = (field: FormBuilderField, allFields: FormBuilderField[]): ValidationError[] => {
  const errors: ValidationError[] = [];

  if (!field.label || field.label.trim() === '') {
    errors.push({ field: 'label', message: 'Field label is required' });
  }

  const nameError = validateFieldName(field.name, allFields, field.id);
  if (nameError) {
    errors.push({ field: 'name', message: nameError });
  }

  if (['select', 'radio', 'checkbox'].includes(field.type)) {
    if (!field.options || field.options.length === 0) {
      errors.push({ field: 'options', message: 'At least one option is required' });
    } else if (field.options.some(opt => !opt || opt.trim() === '')) {
      errors.push({ field: 'options', message: 'Options cannot be empty' });
    }
  }

  if (field.type === 'multiselect' && !field.subtype) {
    errors.push({ field: 'subtype', message: 'Multiselect field must have a subtype' });
  }

  return errors;
};

const BANNER_CONSTRAINTS = {
  recommendedWidth: 1200,
  recommendedHeight: 400,
  maxSizeMB: 2,
  acceptedTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
  acceptedLabels: 'JPG, PNG, or WebP'
};

const FormBuilder: React.FC<FormBuilderProps> = ({ form, onSave, onClose, isOpen, onBannerChange }) => {
  // State
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [defaultLeadSourceId, setDefaultLeadSourceId] = useState<number | null>(null);
  const [leadSources, setLeadSources] = useState<LeadSource[]>([]);
  const [loadingSources, setLoadingSources] = useState(false);
  const [bannerLoading, setBannerLoading] = useState(false);
  const bannerInputRef = useRef<HTMLInputElement>(null);
  const [fields, setFields] = useState<FormBuilderField[]>([]);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [showFieldEditor, setShowFieldEditor] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // History for undo/redo
  const [history, setHistory] = useState<HistoryState[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // Refs
  const initialStateRef = useRef<string>('');

  // Memoized selected field
  const selectedField = useMemo(() => {
    return fields.find(f => f.id === selectedFieldId) || null;
  }, [fields, selectedFieldId]);

  // Initialize form from props (only when modal opens or form changes)
  useEffect(() => {
    // Only initialize when modal is open
    if (!isOpen) return;

    if (!form) {
      // Create default Personal Information section with required fields
      const defaultFields: FormBuilderField[] = [
        { 
          id: generateUniqueId(), 
          name: 'first_name', 
          label: 'First Name', 
          type: 'text', 
          required: true, 
          order: 1, 
          section: 'Personal Information', 
          placeholder: 'Enter your first name' 
        },
        { 
          id: generateUniqueId(), 
          name: 'last_name', 
          label: 'Last Name', 
          type: 'text', 
          required: true, 
          order: 2, 
          section: 'Personal Information', 
          placeholder: 'Enter your last name' 
        },
        { 
          id: generateUniqueId(), 
          name: 'email', 
          label: 'Email', 
          type: 'email', 
          required: true, 
          order: 3, 
          section: 'Personal Information', 
          placeholder: 'Enter your email address' 
        },
        { 
          id: generateUniqueId(), 
          name: 'phone', 
          label: 'Phone Number', 
          type: 'tel', 
          required: false, 
          order: 4, 
          section: 'Personal Information', 
          placeholder: 'Enter your phone number' 
        }
      ];

      const initialState = {
        formName: 'New Form',
        formDescription: '',
        fields: defaultFields,
        defaultLeadSourceId: null
      };
      setFormName(initialState.formName);
      setFormDescription(initialState.formDescription);
      setDefaultLeadSourceId(initialState.defaultLeadSourceId);
      setFields(initialState.fields);
      setSelectedFieldId(null);
      setShowFieldEditor(false);
      setValidationErrors([]);
      setHistory([initialState]);
      setHistoryIndex(0);
      initialStateRef.current = JSON.stringify(initialState);
      return;
    }

    try {
      const structure = form.structure || form.formStructure;
      const extractedFields: FormBuilderField[] = buildFieldsFromFormStructure(
        structure,
        generateUniqueId
      ) as FormBuilderField[];

      const initialState = {
        formName: form.name,
        formDescription: form.description || '',
        fields: extractedFields,
        defaultLeadSourceId: form.defaultLeadSourceId || null
      };

      setFormName(initialState.formName);
      setFormDescription(initialState.formDescription);
      setDefaultLeadSourceId(initialState.defaultLeadSourceId);
      setFields(initialState.fields);
      setSelectedFieldId(null);
      setShowFieldEditor(false);
      setValidationErrors([]);
      setHistory([]);
      setHistoryIndex(-1);
      initialStateRef.current = JSON.stringify(initialState);
    } catch (error) {
      console.error('Error parsing form structure:', error);
      setValidationErrors([{ field: 'form', message: 'Error loading form structure' }]);
    }
  }, [form, isOpen]);

  // Load lead sources when modal opens
  useEffect(() => {
    if (!isOpen) return;
    
    const loadLeadSources = async () => {
      try {
        setLoadingSources(true);
        const response = await api.get('/leads/sources?includeInactive=false');
        if (response.data.success) {
          setLeadSources(response.data.data || []);
        }
      } catch (error) {
        console.error('Failed to load lead sources:', error);
        setLeadSources([]);
      } finally {
        setLoadingSources(false);
      }
    };

    loadLeadSources();
  }, [isOpen]);

  // Track unsaved changes
  useEffect(() => {
    const currentState = JSON.stringify({ formName, formDescription, fields, defaultLeadSourceId });
    setHasUnsavedChanges(currentState !== initialStateRef.current);
  }, [formName, formDescription, fields, defaultLeadSourceId]);

  // Add to history
  const addToHistory = useCallback((state: HistoryState) => {
    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(state);
      // Keep only last 50 states
      return newHistory.slice(-50);
    });
    setHistoryIndex(prev => Math.min(prev + 1, 49));
  }, [historyIndex]);

  // Undo
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      const prevState = history[historyIndex - 1];
      setFormName(prevState.formName);
      setFormDescription(prevState.formDescription);
      setDefaultLeadSourceId(prevState.defaultLeadSourceId || null);
      setFields(prevState.fields);
      setHistoryIndex(prev => prev - 1);
    }
  }, [history, historyIndex]);

  // Redo
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const nextState = history[historyIndex + 1];
      setFormName(nextState.formName);
      setFormDescription(nextState.formDescription);
      setDefaultLeadSourceId(nextState.defaultLeadSourceId || null);
      setFields(nextState.fields);
      setHistoryIndex(prev => prev + 1);
    }
  }, [history, historyIndex]);

  // Function to load Lead Form template with all lead fields
  const loadLeadFormTemplate = useCallback(() => {
    if (fields.length > 4) { // More than just the default Personal Information fields
      if (!window.confirm('Loading Lead Form template will add all lead fields to your form. The Personal Information section will be preserved. Continue?')) {
        return;
      }
    }

    // Auto-set form name if empty
    if (!formName || formName.trim() === '') {
      setFormName('Lead Form');
    }

    const leadFields: FormBuilderField[] = [
      // Keep the existing Personal Information fields (first 4 fields)
      ...fields.slice(0, 4),
      // Additional Personal Information fields
      { id: generateUniqueId(), name: 'title', label: 'Title', type: 'select', required: false, order: 5, section: 'Personal Information', options: ['Mr', 'Mrs', 'Ms', 'Miss', 'Dr', 'Prof', 'Rev', 'Sir', 'Dame'] },
      { id: generateUniqueId(), name: 'mobile_number', label: 'Mobile Number', type: 'tel', required: false, order: 6, section: 'Personal Information', placeholder: 'Enter your mobile number' },
      { id: generateUniqueId(), name: 'alternative_mobile_number', label: 'Alternative Mobile Number', type: 'tel', required: false, order: 7, section: 'Personal Information', placeholder: 'Enter alternative mobile number' },
      { id: generateUniqueId(), name: 'date_of_birth', label: 'Date of Birth', type: 'date', required: false, order: 8, section: 'Personal Information' },
      { id: generateUniqueId(), name: 'gender', label: 'Gender', type: 'select', required: false, order: 9, section: 'Personal Information', options: ['Male', 'Female', 'Other', 'Prefer not to say'] },
      
      // Company/Professional Information Section
      { id: generateUniqueId(), name: 'company', label: 'Company', type: 'text', required: false, order: 10, section: 'Company Information', placeholder: 'Enter company name' },
      { id: generateUniqueId(), name: 'job_title', label: 'Job Title', type: 'text', required: false, order: 11, section: 'Company Information', placeholder: 'Enter your job title' },
      { id: generateUniqueId(), name: 'industry', label: 'Industry', type: 'text', required: false, order: 12, section: 'Company Information', placeholder: 'Enter industry' },
      { id: generateUniqueId(), name: 'website', label: 'Website', type: 'url', required: false, order: 13, section: 'Company Information', placeholder: 'https://example.com' },
      { id: generateUniqueId(), name: 'linkedin_url', label: 'LinkedIn Profile', type: 'url', required: false, order: 14, section: 'Company Information', placeholder: 'https://linkedin.com/in/yourprofile' },
      
      // Address Information Section
      { id: generateUniqueId(), name: 'street_address', label: 'Street Address', type: 'text', required: false, order: 15, section: 'Address Information', placeholder: 'Enter street address' },
      { id: generateUniqueId(), name: 'address_line_1', label: 'Address Line 1', type: 'text', required: false, order: 16, section: 'Address Information', placeholder: 'Enter address line 1' },
      { id: generateUniqueId(), name: 'address_line_2', label: 'Address Line 2', type: 'text', required: false, order: 17, section: 'Address Information', placeholder: 'Enter address line 2' },
      { id: generateUniqueId(), name: 'city', label: 'City', type: 'text', required: false, order: 18, section: 'Address Information', placeholder: 'Enter city' },
      { id: generateUniqueId(), name: 'state', label: 'State/Province', type: 'text', required: false, order: 19, section: 'Address Information', placeholder: 'Enter state or province' },
      { id: generateUniqueId(), name: 'postal_code', label: 'Postal Code', type: 'text', required: false, order: 20, section: 'Address Information', placeholder: 'Enter postal code' },
      { id: generateUniqueId(), name: 'country', label: 'Country', type: 'text', required: false, order: 21, section: 'Address Information', placeholder: 'Enter country' },
      
      // Additional Notes
      { id: generateUniqueId(), name: 'notes', label: 'Notes', type: 'textarea', required: false, order: 22, section: 'Additional Information', placeholder: 'Enter any additional notes' }
    ];

    // Update form name if it was empty
    const updatedFormName = formName || 'Lead Form';
    
    setFormName(updatedFormName);
    setFields(leadFields);
    setSelectedFieldId(null);
    setShowFieldEditor(false);
    
    // Initialize history with the loaded template (first entry)
    const templateState = {
      formName: updatedFormName,
      formDescription,
      fields: leadFields
    };
    setHistory([templateState]);
    setHistoryIndex(0);
    
    // Mark as having unsaved changes (template is different from initial state)
    setHasUnsavedChanges(true);
  }, [fields, formName, formDescription]);

  // Add field
  const addField = useCallback((fieldType: { type: FieldType; label: string; icon: string }) => {
    if (fields.length >= MAX_FIELDS) {
      setValidationErrors([{ field: 'form', message: `Maximum ${MAX_FIELDS} fields allowed` }]);
      return;
    }

    const fieldNumber = fields.length + 1;
    const fieldName = `field_${fieldNumber}`;
    const fieldLabel = fieldType.label;

    const newField: FormBuilderField = {
      id: generateUniqueId(),
      name: fieldName,
      label: fieldLabel,
      type: fieldType.type,
      required: false,
      order: fieldNumber,
      section: DEFAULT_SECTION_NAME,
      placeholder: '',
      options: ['select', 'radio', 'checkbox'].includes(fieldType.type) ? ['Option 1'] : undefined,
      subtype: fieldType.type === 'multiselect' ? 'university' : undefined
    };

    const newFields = [...fields, newField];
    setFields(newFields);
    setSelectedFieldId(newField.id);
    setShowFieldEditor(true);
    
    addToHistory({ formName, formDescription, fields: newFields, defaultLeadSourceId });
  }, [fields, formName, formDescription, defaultLeadSourceId, addToHistory]);

  // Update field
  const updateField = useCallback((updatedField: FormBuilderField) => {
    setFields(prevFields => 
      prevFields.map(field => 
        field.id === updatedField.id ? { ...updatedField } : field
      )
    );
  }, []);

  // Required template field names (matching Lead Form template)
  const REQUIRED_TEMPLATE_FIELDS = ['first_name', 'last_name', 'email'];

  // Delete field
  const deleteField = useCallback((fieldId: string) => {
    const fieldToDelete = fields.find(f => f.id === fieldId);
    if (!fieldToDelete) return;

    // Prevent deletion of required personal information fields
    if (REQUIRED_PERSONAL_FIELDS.includes(fieldToDelete.name)) {
      setValidationErrors([{ 
        field: 'form', 
        message: `"${fieldToDelete.label}" is a required field and cannot be deleted. This field must always be present on the form.` 
      }]);
      return;
    }

    // Check if this is a required template field
    const isRequiredTemplateField = REQUIRED_TEMPLATE_FIELDS.includes(fieldToDelete.name);
    
    if (isRequiredTemplateField) {
      const confirmMessage = `Warning: "${fieldToDelete.label}" is a required field for Lead Form templates.\n\n` +
        `Deleting this field may prevent leads from being created from form submissions.\n\n` +
        `Are you sure you want to delete this field?`;
      
      if (!window.confirm(confirmMessage)) {
        return;
      }
    } else {
      if (!window.confirm(`Are you sure you want to delete the field "${fieldToDelete.label}"?`)) {
        return;
      }
    }

    const newFields = fields.filter(f => f.id !== fieldId);
    setFields(newFields);
    
    if (selectedFieldId === fieldId) {
      setSelectedFieldId(null);
      setShowFieldEditor(false);
    }

    addToHistory({ formName, formDescription, fields: newFields, defaultLeadSourceId });
  }, [fields, selectedFieldId, formName, formDescription, defaultLeadSourceId, addToHistory]);

  // Move field
  const moveField = useCallback((index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= fields.length) return;

    const newFields = [...fields];
    [newFields[index], newFields[targetIndex]] = [newFields[targetIndex], newFields[index]];
    
    // Update order property immutably
    const fieldsWithUpdatedOrder = newFields.map((field, idx) => ({
      ...field,
      order: idx + 1
    }));
    
    setFields(fieldsWithUpdatedOrder);
    addToHistory({ formName, formDescription, fields: fieldsWithUpdatedOrder, defaultLeadSourceId });
  }, [fields, formName, formDescription, defaultLeadSourceId, addToHistory]);

  // Validate all fields
  const validateAllFields = useCallback((): boolean => {
    const errors: ValidationError[] = [];

    if (!formName || formName.trim() === '') {
      errors.push({ field: 'formName', message: 'Form name is required' });
    }

    if (formName.length > MAX_FORM_NAME_LENGTH) {
      errors.push({ field: 'formName', message: `Form name must be less than ${MAX_FORM_NAME_LENGTH} characters` });
    }

    if (fields.length === 0) {
      errors.push({ field: 'fields', message: 'At least one field is required' });
    }

    // Validate Personal Information section requirements
    REQUIRED_PERSONAL_FIELDS.forEach(fieldName => {
      if (!fields.some(f => f.name === fieldName)) {
        const fieldLabels: { [key: string]: string } = {
          'first_name': 'First Name',
          'last_name': 'Last Name',
          'email': 'Email',
          'phone': 'Phone Number'
        };
        errors.push({ 
          field: 'form', 
          message: `${fieldLabels[fieldName] || fieldName} field is required in the Personal Information section` 
        });
      }
    });

    // Check for required template fields if form name suggests it's a lead form
    const isLeadForm = formName && (formName.toLowerCase().includes('lead') || 
                     fields.some(f => REQUIRED_TEMPLATE_FIELDS.includes(f.name)));
    
    if (isLeadForm) {
      const existingFieldNames = fields.map(f => f.name);
      const missingRequiredFields = REQUIRED_TEMPLATE_FIELDS.filter(
        requiredName => !existingFieldNames.includes(requiredName)
      );
      
      if (missingRequiredFields.length > 0) {
        const fieldLabels: { [key: string]: string } = {
          'first_name': 'First Name',
          'last_name': 'Last Name',
          'email': 'Email'
        };
        
        errors.push({
          field: 'form',
          message: `Required Lead Form fields are missing: ${missingRequiredFields.map(f => fieldLabels[f] || f).join(', ')}. These fields are required to create leads from form submissions.`
        });
      }
    }

    fields.forEach(field => {
      const fieldErrors = validateField(field, fields);
      errors.push(...fieldErrors);
    });

    setValidationErrors(errors);
    return errors.length === 0;
  }, [formName, fields]);

  // Transform fields to form structure (shared serializer)
  const transformFieldsToStructure = useCallback((): any => {
    return buildFormStructureFromFields(fields, DEFAULT_SECTION_NAME);
  }, [fields]);

  // Save form
  const handleSave = useCallback(async () => {
    if (!validateAllFields()) {
      return;
    }

    try {
      setIsSaving(true);
      const structure = transformFieldsToStructure();
      
      await onSave({
        name: formName.trim(),
        description: formDescription.trim(),
        formStructure: structure,
        defaultLeadSourceId: defaultLeadSourceId !== null && defaultLeadSourceId !== undefined ? defaultLeadSourceId : null
      });

      // Update initial state after successful save
      initialStateRef.current = JSON.stringify({ formName, formDescription, fields, defaultLeadSourceId });
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error saving form:', error);
      setValidationErrors([{ field: 'form', message: 'Error saving form. Please try again.' }]);
    } finally {
      setIsSaving(false);
    }
  }, [formName, formDescription, fields, defaultLeadSourceId, validateAllFields, transformFieldsToStructure, onSave]);

  // Handle discard changes - reset to initial state
  const handleDiscardChanges = useCallback(() => {
    try {
      const initialState = JSON.parse(initialStateRef.current);
      setFormName(initialState.formName);
      setFormDescription(initialState.formDescription);
      setDefaultLeadSourceId(initialState.defaultLeadSourceId || null);
      setFields(initialState.fields);
      setSelectedFieldId(null);
      setShowFieldEditor(false);
      setValidationErrors([]);
      setHasUnsavedChanges(false);
      setHistory([]);
      setHistoryIndex(-1);
      setShowUnsavedWarning(false);
      onClose();
    } catch (error) {
      console.error('Error resetting form state:', error);
      // Fallback: just close if reset fails
      setShowUnsavedWarning(false);
      onClose();
    }
  }, [onClose]);

  // Handle close with unsaved changes warning
  const handleClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
    } else {
      onClose();
    }
  }, [hasUnsavedChanges, onClose]);

  // Export form structure
  const handleExport = useCallback(() => {
    const structure = transformFieldsToStructure();
    const exportData = {
      name: formName,
      description: formDescription,
      structure
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${sanitizeFieldName(formName)}_form.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [formName, formDescription, transformFieldsToStructure]);

  // Filtered fields for search
  const filteredFields = useMemo(() => {
    if (!searchTerm) return fields;
    
    const search = searchTerm.toLowerCase();
    return fields.filter(field => 
      field.label.toLowerCase().includes(search) ||
      field.name.toLowerCase().includes(search) ||
      field.section?.toLowerCase().includes(search) ||
      field.subsection?.toLowerCase().includes(search)
    );
  }, [fields, searchTerm]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col">
      {/* Header */}
      <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center flex-shrink-0 bg-white shadow-sm">
        <div className="flex items-center space-x-4">
          <h3 className="text-xl font-bold text-gray-900">Form Builder</h3>
          {hasUnsavedChanges && (
            <span className="text-xs text-orange-600 flex items-center bg-orange-50 px-2 py-1 rounded-full">
              <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
              Unsaved changes
            </span>
          )}
        </div>
        <button
          onClick={handleClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close form builder"
        >
          <XMarkIcon className="w-6 h-6" />
        </button>
      </div>

        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mx-6 mt-4">
            <div className="flex">
              <ExclamationTriangleIcon className="w-5 h-5 text-red-500 mr-2" />
              <div>
                <h4 className="text-sm font-medium text-red-800">Validation Errors:</h4>
                <ul className="list-disc list-inside text-sm text-red-700 mt-1">
                  {validationErrors.map((error, idx) => (
                    <li key={idx}>{error.message}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-1 overflow-hidden">
          {/* Left Panel - Field Library & Form Settings */}
          <div className="w-80 border-r border-gray-200 flex flex-col overflow-hidden bg-gray-50">
            <div className="p-6 overflow-y-auto flex-1">
              {/* Form Settings */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-4 text-sm uppercase tracking-wide">Form Settings</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Form Name *
                    </label>
                    <input
                      type="text"
                      value={formName}
                      onChange={(e) => setFormName(e.target.value.slice(0, MAX_FORM_NAME_LENGTH))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter form name"
                      maxLength={MAX_FORM_NAME_LENGTH}
                      aria-label="Form name"
                    />
                    <span className="text-xs text-gray-500">
                      {formName.length}/{MAX_FORM_NAME_LENGTH}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={formDescription}
                      onChange={(e) => setFormDescription(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Optional description"
                      aria-label="Form description"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Default Lead Source
                    </label>
                    <select
                      value={defaultLeadSourceId || ''}
                      onChange={(e) => setDefaultLeadSourceId(e.target.value ? parseInt(e.target.value) : null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      aria-label="Default lead source"
                    >
                      <option value="">None (use default)</option>
                      {leadSources.map(source => (
                        <option key={source.id} value={source.id}>
                          {source.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      When a lead is created from this form's submissions, it will use this source
                    </p>
                  </div>

                  {/* Form Banner (existing form only) */}
                  {form?.id != null && (
                    <div className="border-t border-gray-200 pt-4 mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Form Banner
                      </label>
                      <p className="text-xs text-gray-500 mb-2">
                        Recommended size: <strong>{BANNER_CONSTRAINTS.recommendedWidth} × {BANNER_CONSTRAINTS.recommendedHeight} px</strong>. File types: <strong>{BANNER_CONSTRAINTS.acceptedLabels}</strong>. Max size: <strong>{BANNER_CONSTRAINTS.maxSizeMB} MB</strong>.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-4 items-start">
                        <div className="w-full sm:w-64 h-24 rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden flex-shrink-0">
                          {form.bannerUrl ? (
                            <img src={form.bannerUrl} alt="Form banner" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-xs text-gray-400 text-center px-2">No banner</span>
                          )}
                        </div>
                        <div className="flex flex-col gap-2">
                          <input
                            ref={bannerInputRef}
                            type="file"
                            accept={BANNER_CONSTRAINTS.acceptedTypes.join(',')}
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file || !form?.id) return;
                              if (!BANNER_CONSTRAINTS.acceptedTypes.includes(file.type)) {
                                alert(`Please choose ${BANNER_CONSTRAINTS.acceptedLabels}.`);
                                return;
                              }
                              if (file.size > BANNER_CONSTRAINTS.maxSizeMB * 1024 * 1024) {
                                alert(`File must be ${BANNER_CONSTRAINTS.maxSizeMB} MB or smaller.`);
                                return;
                              }
                              setBannerLoading(true);
                              try {
                                const res = await uploadFormBanner(form.id, file);
                                if (res.success && res.data?.bannerUrl) {
                                  onBannerChange?.(res.data.bannerUrl);
                                }
                                if (bannerInputRef.current) bannerInputRef.current.value = '';
                              } catch (err: any) {
                                alert(err.response?.data?.message || 'Failed to upload banner');
                              } finally {
                                setBannerLoading(false);
                              }
                            }}
                            className="hidden"
                          />
                          <button
                            type="button"
                            onClick={() => bannerInputRef.current?.click()}
                            disabled={bannerLoading}
                            className="px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-600 rounded-md hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {bannerLoading ? 'Uploading...' : form.bannerUrl ? 'Replace banner' : 'Upload banner'}
                          </button>
                          {form.bannerUrl && (
                            <button
                              type="button"
                              onClick={async () => {
                                if (!form?.id) return;
                                setBannerLoading(true);
                                try {
                                  await removeFormBanner(form.id);
                                  onBannerChange?.(null);
                                } catch (err: any) {
                                  alert(err.response?.data?.message || 'Failed to remove banner');
                                } finally {
                                  setBannerLoading(false);
                                }
                              }}
                              disabled={bannerLoading}
                              className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Remove banner
                            </button>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        This image appears as the header on the public form page.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Personal Information Notice */}
              <div className="mb-6 border-t border-gray-200 pt-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h5 className="font-semibold text-blue-900 text-xs mb-2 flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Default Fields
                  </h5>
                  <ul className="text-xs text-blue-800 space-y-1">
                    <li>• First Name, Last Name, Email & Phone are always present</li>
                    <li>• These fields cannot be deleted</li>
                    <li>• You can customize their settings</li>
                  </ul>
                </div>
              </div>

              {/* Quick Templates */}
              <div className="mb-6 border-t border-gray-200 pt-6">
                <h4 className="font-semibold text-gray-900 mb-3 text-sm uppercase tracking-wide">Quick Templates</h4>
                <button
                  onClick={loadLeadFormTemplate}
                  className="w-full px-3 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-md hover:from-blue-700 hover:to-indigo-700 flex items-center justify-center space-x-2 transition-all shadow-sm hover:shadow-md font-medium"
                  aria-label="Load Lead Form Template"
                >
                  <span className="text-lg">📋</span>
                  <span className="text-sm">Lead Form Template</span>
                </button>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Pre-fill form with all lead fields
                </p>
              </div>

              {/* Field Types */}
              <FormBuilderFieldLibrary
                onAddField={addField}
                fieldCount={fields.length}
                maxFields={MAX_FIELDS}
              />
            </div>
          </div>

          {/* Middle Panel - Form Structure */}
          <div className="flex-1 flex flex-col overflow-hidden bg-white">
            {/* Toolbar */}
            <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center flex-shrink-0 bg-white">
              <div className="flex space-x-2">
                <button
                  onClick={() => setPreviewMode(false)}
                  className={`px-3 py-2 rounded-md flex items-center space-x-2 transition-colors ${
                    !previewMode ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  aria-label="Build mode"
                >
                  <Cog6ToothIcon className="w-4 h-4" />
                  <span>Build</span>
                </button>
                <button
                  onClick={() => setPreviewMode(true)}
                  className={`px-3 py-2 rounded-md flex items-center space-x-2 transition-colors ${
                    previewMode ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  aria-label="Preview mode"
                >
                  <EyeIcon className="w-4 h-4" />
                  <span>Preview</span>
                </button>
                <div className="border-l border-gray-300 mx-2" />
                <button
                  onClick={handleUndo}
                  disabled={historyIndex <= 0}
                  className="px-3 py-2 rounded-md text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Undo"
                  aria-label="Undo"
                >
                  <ArrowUturnLeftIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={handleRedo}
                  disabled={historyIndex >= history.length - 1}
                  className="px-3 py-2 rounded-md text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Redo"
                  aria-label="Redo"
                >
                  <ArrowUturnLeftIcon className="w-4 h-4 transform scale-x-[-1]" />
                </button>
                <button
                  onClick={handleExport}
                  className="px-3 py-2 rounded-md text-gray-600 hover:bg-gray-100"
                  title="Export form structure"
                  aria-label="Export"
                >
                  <DocumentArrowDownIcon className="w-4 h-4" />
                </button>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={handleClose}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 transition-colors"
                >
                  {isSaving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="w-4 h-4" />
                      <span>Save Form</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Form Content */}
            <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
              {previewMode ? (
                <FormPreview fields={fields} formName={formName} formDescription={formDescription} />
              ) : (
                <>
                  {/* Search */}
                  {fields.length > 5 && (
                    <div className="mb-6">
                      <div className="relative">
                        <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-2.5 text-gray-400" />
                        <input
                          type="text"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          placeholder="Search fields..."
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          aria-label="Search fields"
                        />
                      </div>
                    </div>
                  )}
                  <FormStructure
                    fields={filteredFields}
                    selectedFieldId={selectedFieldId}
                    onFieldSelect={(field) => {
                      setSelectedFieldId(field.id);
                      setShowFieldEditor(true);
                    }}
                    onFieldDelete={deleteField}
                    onFieldMove={(index, direction) => {
                      const actualIndex = fields.findIndex(f => f.id === filteredFields[index].id);
                      moveField(actualIndex, direction);
                    }}
                  />
                </>
              )}
            </div>
          </div>

          {/* Right Panel - Field Editor */}
          {showFieldEditor && selectedField && (
            <div className="w-80 border-l border-gray-200 flex flex-col overflow-hidden bg-gray-50">
              <FieldEditor
                field={selectedField}
                allFields={fields}
                onUpdate={updateField}
                onClose={() => {
                  setShowFieldEditor(false);
                  setSelectedFieldId(null);
                }}
              />
            </div>
          )}
        </div>

      {/* Unsaved Changes Warning Modal */}
      {showUnsavedWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md">
            <h3 className="text-lg font-semibold mb-4">Unsaved Changes</h3>
            <p className="text-gray-600 mb-6">
              You have unsaved changes. Are you sure you want to close without saving?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowUnsavedWarning(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Save Changes
              </button>
              <button
                onClick={handleDiscardChanges}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Form Structure Component
const FormStructure: React.FC<{
  fields: FormBuilderField[];
  selectedFieldId: string | null;
  onFieldSelect: (field: FormBuilderField) => void;
  onFieldDelete: (fieldId: string) => void;
  onFieldMove: (index: number, direction: 'up' | 'down') => void;
}> = ({ fields, selectedFieldId, onFieldSelect, onFieldDelete, onFieldMove }) => {
  if (fields.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
        <Cog6ToothIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-500">No fields added yet.</p>
        <p className="text-sm text-gray-400 mt-1">Start by adding fields from the left panel.</p>
      </div>
    );
  }

  // Group by section
  const sectionsMap = new Map<string, FormBuilderField[]>();
  fields.forEach(field => {
    const section = field.section || DEFAULT_SECTION_NAME;
    if (!sectionsMap.has(section)) {
      sectionsMap.set(section, []);
    }
    sectionsMap.get(section)!.push(field);
  });

  return (
    <div className="space-y-6">
      {Array.from(sectionsMap.entries()).map(([sectionName, sectionFields]) => (
        <div key={sectionName} className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-medium text-gray-900 mb-3">{sectionName}</h3>
          <div className="space-y-2">
            {sectionFields.map((field, sectionIndex) => {
              const globalIndex = fields.findIndex(f => f.id === field.id);
              const isSelected = field.id === selectedFieldId;
              
              const isProtectedField = REQUIRED_PERSONAL_FIELDS.includes(field.name);

              return (
                <div
                  key={field.id}
                  className={`border rounded-md p-3 bg-white cursor-pointer transition-all ${
                    isSelected 
                      ? 'border-blue-500 ring-2 ring-blue-200 shadow-md' 
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                  }`}
                  onClick={() => onFieldSelect(field)}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      onFieldSelect(field);
                    }
                  }}
                  aria-label={`Field: ${field.label}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {field.label}
                        </span>
                        {field.required && (
                          <span className="text-red-500 text-xs font-bold">*</span>
                        )}
                        {isProtectedField && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                            Default
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className="text-xs text-gray-500 capitalize bg-gray-100 px-2 py-0.5 rounded">
                          {field.type}
                        </span>
                        <span className="text-xs text-gray-400">
                          {field.name}
                        </span>
                        {field.subsection && (
                          <span className="text-xs text-gray-400">
                            • {field.subsection}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-1 ml-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onFieldMove(globalIndex, 'up');
                        }}
                        disabled={globalIndex === 0}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        title="Move up"
                        aria-label="Move field up"
                      >
                        <ArrowUpIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onFieldMove(globalIndex, 'down');
                        }}
                        disabled={globalIndex === fields.length - 1}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        title="Move down"
                        aria-label="Move field down"
                      >
                        <ArrowDownIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onFieldDelete(field.id);
                        }}
                        disabled={isProtectedField}
                        className={`p-1 transition-colors ${
                          isProtectedField 
                            ? 'text-gray-300 cursor-not-allowed' 
                            : 'text-red-400 hover:text-red-600'
                        }`}
                        title={isProtectedField ? "This field cannot be deleted" : "Delete field"}
                        aria-label="Delete field"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

// Field Editor Component with debouncing
const FieldEditor: React.FC<{
  field: FormBuilderField;
  allFields: FormBuilderField[];
  onUpdate: (field: FormBuilderField) => void;
  onClose: () => void;
}> = ({ field, allFields, onUpdate, onClose }) => {
  const [editedField, setEditedField] = useState<FormBuilderField>({ ...field });
  const [fieldErrors, setFieldErrors] = useState<ValidationError[]>([]);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Get unique sections and subsections from all fields
  const existingSections = useMemo(() => {
    const sections = new Set<string>();
    allFields.forEach(f => {
      if (f.section) sections.add(f.section);
    });
    return Array.from(sections).sort();
  }, [allFields]);

  const existingSubsections = useMemo(() => {
    const subsections = new Set<string>();
    allFields.forEach(f => {
      if (f.subsection && f.section === editedField.section) {
        subsections.add(f.subsection);
      }
    });
    return Array.from(subsections).sort();
  }, [allFields, editedField.section]);

  useEffect(() => {
    setEditedField({ ...field });
  }, [field]);

  useEffect(() => {
    const errors = validateField(editedField, allFields);
    setFieldErrors(errors);
  }, [editedField, allFields]);

  const debouncedUpdate = useCallback((updated: FormBuilderField) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(() => {
      onUpdate(updated);
    }, DEBOUNCE_DELAY);
  }, [onUpdate]);

  const handleChange = (key: keyof FormBuilderField, value: any) => {
    const updated = {
      ...editedField,
      [key]: value
    };
    
    setEditedField(updated);
    debouncedUpdate(updated);
  };

  const addOption = () => {
    const options = editedField.options || [];
    handleChange('options', [...options, `Option ${options.length + 1}`]);
  };

  const updateOption = (index: number, value: string) => {
    const options = [...(editedField.options || [])];
    options[index] = value;
    handleChange('options', options);
  };

  const removeOption = (index: number) => {
    const options = [...(editedField.options || [])];
    if (options.length <= 1) {
      return; // Don't allow removing last option
    }
    options.splice(index, 1);
    handleChange('options', options);
  };

  const needsOptions = ['select', 'radio', 'checkbox'].includes(editedField.type);

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0 bg-white">
        <h4 className="font-semibold text-gray-900">Field Settings</h4>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close field editor"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Field Errors */}
      {fieldErrors.length > 0 && (
        <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="w-4 h-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-red-700">
              <ul className="list-disc list-inside">
                {fieldErrors.map((error, idx) => (
                  <li key={idx}>{error.message}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {/* Info Notice for Protected Fields */}
        {REQUIRED_PERSONAL_FIELDS.includes(editedField.name) && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-1">Default Field</p>
                <p className="text-xs">This field is included by default and cannot be deleted. You can customize its settings.</p>
              </div>
            </div>
          </div>
        )}

        {/* Field Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Field Type
          </label>
          <div className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-md capitalize">
            {editedField.type}
          </div>
        </div>

        {/* Field Label */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Field Label *
          </label>
          <input
            type="text"
            value={editedField.label || ''}
            onChange={(e) => handleChange('label', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter field label"
            aria-label="Field label"
          />
        </div>

        {/* Field Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Field Name (Internal) *
          </label>
          <input
            type="text"
            value={editedField.name || ''}
            readOnly
            disabled
            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 font-mono text-sm text-gray-600 cursor-not-allowed"
            placeholder="field_name"
            aria-label="Field name"
          />
          <p className="text-xs text-gray-500 mt-1">
            Field name cannot be changed after creation
          </p>
        </div>

        {/* Placeholder */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Placeholder
          </label>
          <input
            type="text"
            value={editedField.placeholder || ''}
            onChange={(e) => handleChange('placeholder', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter placeholder text"
            aria-label="Placeholder"
          />
        </div>

        {/* Section */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Section *
          </label>
          {REQUIRED_PERSONAL_FIELDS.includes(editedField.name) ? (
            // Protected fields - section is locked to "Personal Information"
            <>
              <input
                type="text"
                value="Personal Information"
                readOnly
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed"
                aria-label="Section name"
              />
              <p className="text-xs text-gray-500 mt-1">
                Default fields must stay in the Personal Information section
              </p>
            </>
          ) : (
            // Regular fields - section is editable with suggestions
            <>
              {existingSections.length > 0 && (
                <div className="mb-2">
                  <p className="text-xs text-gray-600 mb-1">Existing sections:</p>
                  <div className="flex flex-wrap gap-1">
                    {existingSections.map((section) => (
                      <button
                        key={section}
                        type="button"
                        onClick={() => handleChange('section', section)}
                        className={`text-xs px-2 py-1 rounded-full transition-colors ${
                          editedField.section === section
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {section}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <input
                type="text"
                value={editedField.section || ''}
                onChange={(e) => handleChange('section', e.target.value.slice(0, MAX_SECTION_NAME_LENGTH))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Type to create a new section..."
                maxLength={MAX_SECTION_NAME_LENGTH}
                aria-label="Section name"
                list="section-suggestions"
              />
              <datalist id="section-suggestions">
                {existingSections.map((section) => (
                  <option key={section} value={section} />
                ))}
              </datalist>
              <p className="text-xs text-gray-500 mt-1">
                Select an existing section above or type a new one
              </p>
            </>
          )}
        </div>

        {/* Subsection */}
        {!REQUIRED_PERSONAL_FIELDS.includes(editedField.name) && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subsection (Optional)
            </label>
            {existingSubsections.length > 0 && (
              <div className="mb-2">
                <p className="text-xs text-gray-600 mb-1">Existing subsections in {editedField.section}:</p>
                <div className="flex flex-wrap gap-1">
                  {existingSubsections.map((subsection) => (
                    <button
                      key={subsection}
                      type="button"
                      onClick={() => handleChange('subsection', subsection)}
                      className={`text-xs px-2 py-1 rounded-full transition-colors ${
                        editedField.subsection === subsection
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {subsection}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <input
              type="text"
              value={editedField.subsection || ''}
              onChange={(e) => handleChange('subsection', e.target.value.slice(0, MAX_SECTION_NAME_LENGTH))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Type to create a new subsection..."
              maxLength={MAX_SECTION_NAME_LENGTH}
              aria-label="Subsection name"
              list="subsection-suggestions"
            />
            <datalist id="subsection-suggestions">
              {existingSubsections.map((subsection) => (
                <option key={subsection} value={subsection} />
              ))}
            </datalist>
            <p className="text-xs text-gray-500 mt-1">
              {existingSubsections.length > 0 ? 'Select existing or type a new one' : 'Leave empty for no subsection'}
            </p>
          </div>
        )}

        {/* Required */}
        <div className="flex items-center">
          <input
            type="checkbox"
            id="required"
            checked={editedField.required || false}
            onChange={(e) => handleChange('required', e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="required" className="ml-2 text-sm font-medium text-gray-700">
            Required Field
          </label>
        </div>

        {/* Multiselect Subtype */}
        {editedField.type === 'multiselect' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Multiselect Type *
            </label>
            <select
              value={editedField.subtype || 'university'}
              onChange={(e) => handleChange('subtype', e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="university">University</option>
              <option value="country">Country</option>
              <option value="city">City</option>
            </select>
          </div>
        )}

        {/* Options for select, radio, checkbox */}
        {needsOptions && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Options *
            </label>
            <div className="space-y-2">
              {(editedField.options || []).map((option, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={option || ''}
                    onChange={(e) => updateOption(index, e.target.value)}
                    placeholder={`Option ${index + 1}`}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    aria-label={`Option ${index + 1}`}
                  />
                  <button
                    onClick={() => removeOption(index)}
                    disabled={(editedField.options?.length || 0) <= 1}
                    className="p-2 text-red-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="Remove option"
                    aria-label="Remove option"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                onClick={addOption}
                className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-md text-gray-600 hover:border-blue-400 hover:text-blue-600 flex items-center justify-center space-x-2 transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                <span className="text-sm">Add Option</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Form Preview Component with section grouping
const FormPreview: React.FC<{
  fields: FormBuilderField[];
  formName: string;
  formDescription: string;
}> = ({ fields, formName, formDescription }) => {
  // Group by section and subsection
  const sectionsMap = new Map<string, Map<string | null, FormBuilderField[]>>();
  
  fields.forEach(field => {
    const section = field.section || DEFAULT_SECTION_NAME;
    const subsection = field.subsection || null;
    
    if (!sectionsMap.has(section)) {
      sectionsMap.set(section, new Map());
    }
    
    const sectionMap = sectionsMap.get(section)!;
    if (!sectionMap.has(subsection)) {
      sectionMap.set(subsection, []);
    }
    
    sectionMap.get(subsection)!.push(field);
  });

  return (
    <div className="max-w-5xl mx-auto">
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">{formName || 'Untitled Form'}</h2>
        {formDescription && (
          <p className="text-gray-600 mb-6">{formDescription}</p>
        )}
        
        {fields.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <EyeIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">No fields to preview</p>
            <p className="text-sm text-gray-400 mt-1">Add fields to see the preview</p>
          </div>
        ) : (
          <div className="space-y-8">
            {Array.from(sectionsMap.entries()).map(([sectionName, subsectionsMap]) => (
              <div key={sectionName} className="border-t border-gray-200 pt-6 first:border-t-0 first:pt-0">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{sectionName}</h3>
                
                {Array.from(subsectionsMap.entries()).map(([subsectionName, subsectionFields]) => (
                  <div key={subsectionName || 'main'} className={subsectionName ? 'ml-4 mb-6' : ''}>
                    {subsectionName && (
                      <h4 className="text-md font-medium text-gray-700 mb-3">{subsectionName}</h4>
                    )}
                    
                    <div className="space-y-4">
                      {subsectionFields.sort((a, b) => (a.order || 0) - (b.order || 0)).map((field) => {
                        const isCountryField = field.subtype === 'country';
                        const isCityField = field.subtype === 'city';
                        const isUniversityField = field.subtype === 'university';
                        const isMultiSelectField = field.type === 'multiselect';
                        
                        return (
                          <div key={field.id}>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {field.label}
                              {field.required && <span className="text-red-500 ml-1">*</span>}
                            </label>
                            
                            {isMultiSelectField ? (
                              <MultiSelectAutoComplete
                                type={field.subtype || 'university'}
                                values={[]}
                                onChange={() => {}}
                                placeholder={field.placeholder || `Select ${field.label?.toLowerCase()}...`}
                                disabled={true}
                                className="w-full opacity-75"
                              />
                            ) : isCountryField || field.name?.toLowerCase().includes('country') ? (
                              <AutoComplete
                                type="country"
                                value=""
                                onChange={() => {}}
                                placeholder={field.placeholder || `Select ${field.label?.toLowerCase()}...`}
                                disabled={true}
                                className="w-full opacity-75"
                              />
                            ) : field.type === 'textarea' ? (
                              <textarea
                                placeholder={field.placeholder}
                                rows={3}
                                disabled
                                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                                aria-label={field.label}
                              />
                            ) : field.type === 'select' ? (
                              <select
                                disabled
                                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                                aria-label={field.label}
                              >
                                <option value="">Select an option</option>
                                {(field.options || []).map((option, index) => (
                                  <option key={index} value={option}>{option}</option>
                                ))}
                              </select>
                            ) : field.type === 'radio' ? (
                              <div className="space-y-2">
                                {(field.options || []).map((option, index) => (
                                  <div key={index} className="flex items-center">
                                    <input
                                      type="radio"
                                      name={field.name}
                                      disabled
                                      className="h-4 w-4 text-blue-600 border-gray-300"
                                      aria-label={option}
                                    />
                                    <label className="ml-2 text-sm text-gray-700">{option}</label>
                                  </div>
                                ))}
                              </div>
                            ) : field.type === 'checkbox' ? (
                              <div className="space-y-2">
                                {(field.options || []).map((option, index) => (
                                  <div key={index} className="flex items-center">
                                    <input
                                      type="checkbox"
                                      disabled
                                      className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                                      aria-label={option}
                                    />
                                    <label className="ml-2 text-sm text-gray-700">{option}</label>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <input
                                type={field.type}
                                placeholder={field.placeholder}
                                disabled
                                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                                aria-label={field.label}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
        
        <div className="mt-8 pt-6 border-t border-gray-200">
          <button
            type="button"
            disabled
            className="px-6 py-3 bg-gray-300 text-gray-500 rounded-md cursor-not-allowed font-medium"
          >
            Submit (Preview Only)
          </button>
        </div>
      </div>
    </div>
  );
};

export default FormBuilder;
